/**
 * Dispatched whenever the socket connection is changed
 */
export const CONNECTION_STATUS_CHANGED = "DREAMDB_CONNECTION_STATUS_CHANGED";

/**
 * Dispatched when master tab is changed
 */
export const MASTER_CHANGED = "DREAMDB_MASTER_CHANGED";

/**
 * Dispatched when authentication status is changed
 */
export const AUTH_STATUS_CHANGED = "DREAMDB_AUTH_STATUS_CHANGED";

/**
 * Dispatched when document is successfully added/updated/deleted in the local database
 * In case of the multiple docs write, it's dispatched if any one document is written successfully in local.
 */
export const WRITE_DOCS_LOCAL_COMPLETED = "DREAMDB_WRITE_DOCS_LOCAL_COMPLETED";

/**
 * Dispatched when document is failed add/update/delete in the local database
 * In case of the multiple docs write, it's dispatched when all the documents are failed to write in local.
 */
export const WRITE_DOCS_LOCAL_FAILED = "DREAMDB_WRITE_DOCS_LOCAL_FAILED";

/**
 * Dispatched when document is successfully added/updated/deleted in the remote database
 */
export const WRITE_DOCS_REMOTE_COMPLETED = "DREAMDB_WRITE_DOCS_REMOTE_COMPLETED";

/**
 * Dispatched when document is failed add/update/delete in the remote database
 */
export const WRITE_DOCS_REMOTE_FAILED = "DREAMDB_WRITE_DOCS_REMOTE_FAILED";

/**
 * Dispatched after local writes(add/update/delete) and remote writes(add/update/delete)
 */
export const WRITE_DOCS_METADATA = "DREAMDB_WRITE_DOCS_METADATA";

/**
 * Dispatched by query when docs updated/deleted which are matched query criteria before updated but after isn't 
 * matched query criteria.
 */
export const DOCS_UPDATED = "DREAMDB_DOCS_UPDATED";

/**
 * Dispatched when user query something. e.g. find
 */
export const QUERY = "DREAMDB_QUERY";

/**
 * Dispatched when user updates existing query.
 * e.g. db.getByIds([1,2])
 *      db.update([1])
 */
export const QUERY_UPDATE = "DREAMDB_QUERY_UPDATE";

/**
 * Dispatched when query is canceled.
 */
export const QUERY_CANCEL = "DREAMDB_QUERY_CANCEL";

/**
 * Dispatched on intial query request if result is found from the local db.
 * Also dispatched whenever the query result is updated
 */
export const QUERY_SNAPSHOT = "DREAMDB_QUERY_SNAPSHOT";

/**
 * Dispatched when metadata is updated.
 * Currently dispatched on remote db's query acknowledgement or on remote query failed
 */
export const QUERY_METADATA = "DREAMDB_QUERY_METADATA";

/**
 * Dispatched when user calls destory method.
 */
export const DESTROY = "DREAMDB_DESTROY";

/**
 * Dispatched when database is deleted from the couchdb.
 */
export const DATABASE_DELETED = "DREAMDB_DATABASE_DELETED";

/**
 * It updates current connection status.
 * @param {Object} object 
 */
export const connectionStatusChanged = ({ couchdbName, connected, connectionLost, error }) => {
  return {
    type: CONNECTION_STATUS_CHANGED,
    couchdbName,
    connected,
    connectionLost,
    error
  }
};

/**
 * It updates master for current session.
 * @param {Object} object 
 */
export const masterChanged = ({ couchdbName, master }) => {
  return {
    type: MASTER_CHANGED,
    couchdbName,
    master
  }
};

/**
 * It updates auth status.
 * @param {Object} object 
 */
export const authChanged = ({ couchdbName, status, error }) => {
  return {
    type: AUTH_STATUS_CHANGED,
    couchdbName,
    status,
    error
  }
};

/**
 * It adds/updates document at `docs` and `pendingWrites` path which are successfully written in local db.
 * It adds document id at `failedWrites` path for the failed documents
 * @param {Object} object 
 */
export const writeDocsLocalCompleted = ({ couchdbName, dbName, requestedAt, docs }) => {
  return {
    type: WRITE_DOCS_LOCAL_COMPLETED,
    couchdbName,
    dbName,
    requestedAt,
    docs
  }
};

/**
 * It adds document id at `failedWrites` path for the failed documents.
 * NOTE: It will skip the documents which are requested for the add.
 */
export const writeDocsLocalFailed = ({ couchdbName, dbName, reqDocs, error }) => {
  return {
    type: WRITE_DOCS_LOCAL_FAILED,
    couchdbName,
    dbName,
    reqDocs,
    error
  }
};

/**
 * It adds/updates document at `docs` and removes docId from `pendingWrites` path which are successfully written in remove db.
 * @param {Object} object 
 */
export const writeDocsRemoteCompleted = ({ couchdbName, dbName, reqDocs, failedWrites }) => {
  return {
    type: WRITE_DOCS_REMOTE_COMPLETED,
    couchdbName,
    dbName,
    reqDocs,
    failedWrites
  }
};

/**
 * It adds document id at `failedWrites` path for the failed documents.
 * @param {Object} object 
 */
export const writeDocsRemoteFailed = ({ couchdbName, dbName, reqDocs, error }) => {
  return {
    type: WRITE_DOCS_REMOTE_FAILED,
    couchdbName,
    dbName,
    reqDocs,
    error
  }
};

/**
 * It updates pendingWrites and failedWrites documents
 * @param {Object} object 
 */
export const writeDocsMetadata = ({ couchdbName, dbName, pendingDocs, failedDocs }) => {
  return {
    type: WRITE_DOCS_METADATA,
    couchdbName,
    dbName,
    pendingDocs,
    failedDocs
  };
};

/**
 * It updates given docs into redux state.
 * @param {Object} object 
 */
export const docsUpdated = ({ couchdbName, dbName, docs }) => {
  return {
    type: DOCS_UPDATED,
    couchdbName,
    dbName,
    docs
  };
};

/**
 * It sets requested queries data at particular path
 * @param {Object} object 
 */
export const query = ({ couchdbName, dbName, query }) => {
  return {
    type: QUERY,
    couchdbName,
    dbName,
    query
  }
};

/**
 * It updates requested query's criteria.
 * @param {Object} object 
 */
export const queryUpdate = ({ couchdbName, dbName, query }) => {
  return {
    type: QUERY_UPDATE,
    couchdbName,
    dbName,
    query
  }
};

/**
 * It just sets live = false. It doesn't removes query data.
 * @param {Object} object 
 */
export const queryCancel = ({ couchdbName, dbName, query }) => {
  return {
    type: QUERY_CANCEL,
    couchdbName,
    dbName,
    query
  }
};

/**
 * It updates query `value` whenever query result is changed.
 * @param {Object} object 
 */
export const querySnapshot = ({ couchdbName, dbName, query, value }) => {
  return {
    type: QUERY_SNAPSHOT,
    couchdbName,
    dbName,
    query,
    value
  }
};

/**
 * It updates metadata of query.
 * @param {Object} object 
 */
export const queryMetadata = ({ couchdbName, dbName, query, metadata }) => {
  return {
    type: QUERY_METADATA,
    couchdbName,
    dbName,
    query,
    metadata
  }
};

/**
 * It removes all data of given couchdb.
 * @param {Object} object 
 */
export const destroy = ({ couchdbName }) => {
  return {
    type: DESTROY,
    couchdbName
  }
};

/**
 * It's used by `CouchDB` when database is deleted from couchdb. 
 * @param {Object} object 
 * @returns 
 */
export const databaseDeleted = ({ couchdbName }) => {
  return {
    type: DATABASE_DELETED,
    couchdbName,
    dbName
  }
};